import { Button, Center, Group, Loader, Paper, Stack, Text } from '@mantine/core';
import { Navigate, useLocation } from 'react-router-dom';
import { useState, type ReactNode } from 'react';
import { useAuthStore } from './authStore';
import { refreshClaims, signOut } from './AuthProvider';
import { tokens as T } from '../theme/tokens';

/** Fails closed, and distinguishes the three ways it can fail: no credential,
 *  a credential without the claim, and a build that cannot check at all. */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const phase = useAuthStore((s) => s.phase);
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  const error = useAuthStore((s) => s.error);
  const location = useLocation();

  if (phase === 'loading') {
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  }

  if (phase === 'unconfigured') {
    return (
      <Center h="100vh" style={{ background: T.page }}>
        <Paper radius="xl" p={32} maw={480}>
          <Stack gap={8}>
            <Text fz={18} fw={800}>
              Not configured
            </Text>
            <Text fz={13} c={T.ink3} lh={1.6}>
              This build carries no Firebase settings, so it cannot establish who you are. That is a
              build problem, not a permission one.
            </Text>
          </Stack>
        </Paper>
      </Center>
    );
  }

  if (phase === 'signedOut') {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isSuperAdmin) {
    return <NotAnOperator error={error} />;
  }

  return <>{children}</>;
}

/** The refusal screen, and the two ways out of it.
 *
 *  It needs both, because without them this is a dead end: the header that
 *  carries the user menu lives inside the Shell, which never renders here — so
 *  an account without the claim could not sign out, switch account, or retry.
 *
 *  "Check again" is the PRIMARY action and the one that usually works. Custom
 *  claims are baked into the ID token, so an operator granted the claim a
 *  moment ago is holding a token minted before the grant and stays refused for
 *  up to an hour. Forcing a refresh is what fixes that, and it is strictly
 *  better than the "sign out and back in" the copy used to advise. */
function NotAnOperator({ error }: { error: string | null }) {
  const email = useAuthStore((s) => s.email);
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState(false);

  async function check() {
    setBusy(true);
    try {
      await refreshClaims();
      // If the claim had landed, this component would already have unmounted.
      // Still here means the token really does lack it.
      setChecked(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Center h="100vh" style={{ background: T.page }}>
      <Paper radius="xl" p={32} maw={560}>
        <Stack gap={10}>
          <Text fz={18} fw={800}>
            {error ? 'Could not check your access' : 'Not an operator'}
          </Text>
          {email ? (
            <Text fz={13} c={T.ink4}>
              Signed in as <Text component="span" fw={700} c={T.ink3}>{email}</Text>
            </Text>
          ) : null}
          <Text fz={13} c={T.ink3} lh={1.6}>
            {error ??
              'This account has no superAdmin claim. An operator grants one with `haus user claims set --email you --super-admin --apply`.'}
          </Text>
          {checked ? (
            <Text fz={13} c={T.coralDeep} lh={1.6}>
              Still no claim on a freshly minted token — so this is the account's access, not a
              stale session. Check that the claim was granted to this exact address, or sign in
              with the operator account.
            </Text>
          ) : (
            <Text fz={12} c={T.ink4} lh={1.6}>
              If the claim was just granted, this session is still holding the token it was issued
              before that. Check again to mint a new one.
            </Text>
          )}
          <Group gap={10} mt={6}>
            <Button loading={busy} onClick={() => void check()}>
              Check again
            </Button>
            <Button variant="default" onClick={() => void signOut()}>
              Sign out
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Center>
  );
}
